import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FaAppStore, FaCloud, FaFile, FaGoogle, FaGraduationCap, FaNetworkWired, FaRobot } from 'react-icons/fa';

const Education = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const education = [
    {
      id: 1,
      degree: 'Master of Science in Computer Science',
      school: 'University of Colorado Boulder',
      period: '2024 - 2026',
      gpa: '3.8 / 4.0',
      location: 'Boulder, CO',
      description: 'Specialization in Software Systems and Cloud Computing',
      courses: [
        'Distributed Systems',
        'Computer Security & Ethical Hacking',
        'Design & Analysis of Algorithms',
        'Network Systems',
        'Database Systems',
        'Natural Language Processing',
      ],
      achievements: [
        'Teaching Assistant - Object Oriented Analysis & Design',
        'Completed 5+ projects involving cloud, security, and distributed systems',
        'Participated in 3+ hackathons and coding competitions'
      ],
      logo: <FaGraduationCap />,
      color: '#CFB87C'
    },
    {
      id: 2,
      degree: 'Bachelor of Engineering in Computer Engineering',
      school: 'Mumbai University',
      period: '2018 - 2022',
      gpa: '9.6 / 10.0',
      location: 'India',
      courses: [
        'Data Structures & Algorithms',
        'Mobile Computing and Communications',
        'Artificial Intelligence',
        'Computer Graphics',
        'Human Computer Interaction',
        'Computer Organization & Architecture',
        'Operating Systems',
        'Computer Networks',
        'Software Engineering',
        'Database Management Systems',
        'Big Data Architecture'
      ],
      achievements: [
        'Third Prize - Ingress Hackathon 2020',
        'Vice President - Institute Innovation Council (IIC)',
        'Academic Excellence Award - for scoring 10 CGPA in final year'
      ],
      logo: <FaGraduationCap />,
      color: '#00ff41'
    }
  ];

  const certifications = [
    { name: 'Introduction to Google SEO', issuer: 'Coursera', icon: <FaGoogle /> },
    { name: 'Computer Networks and Network Security', issuer: 'Coursera', icon: <FaCloud /> },
    { name: 'Machine Learning', issuer: 'Coursera', icon: <FaRobot /> },
    { name: 'Blockchain Basics', issuer: 'Coursera', icon: <FaNetworkWired />},
    { name: 'Smart Contracts', issuer: 'Coursera', icon: <FaFile /> },
    { name: 'Decentralized Applications', issuer: 'Coursera', icon: <FaAppStore /> }

  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: [0.6, -0.05, 0.01, 0.99] }
    }
  };

  return (
    <section className="education" id="education" ref={ref}>
      <div className="container">
        <motion.div
          className="section-header"
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <span className="section-tag">
            <span className="tag-bracket">{'<'}</span>
            education
            <span className="tag-bracket">{'/>'}</span>
          </span>
          <h2 className="section-title">
            <span className="keyword">Academic</span> Background<span className="cursor-blink">.</span>
          </h2>
          <p className="section-description">
            Building knowledge foundations through rigorous academic pursuit
          </p>
        </motion.div>

        <motion.div
          className="education-grid"
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          {education.map((edu, index) => (
            <motion.div
              key={edu.id}
              className="education-card"
              variants={itemVariants}
              whileHover={{ y: -10, scale: 1.02 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <motion.div
                className="card-glow"
                animate={{
                  opacity: [0.3, 0.6, 0.3],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
                style={{ background: `radial-gradient(circle, ${edu.color}40, transparent)` }}
              />

              <div className="edu-header">
                <motion.div
                  className="edu-logo"
                  whileHover={{ rotate: 360, scale: 1.2 }}
                  transition={{ duration: 0.6 }}
                >
                  {edu.logo}
                </motion.div>
                <div className="edu-title-section">
                  <h3 className="edu-degree">{edu.degree}</h3>
                  <h4 className="edu-school">{edu.school}</h4>
                  <div className="edu-meta">
                    <span className="edu-period"> {edu.period}</span>
                    <span className="edu-location"> {edu.location}</span>
                    <span className="edu-gpa" style={{ color: edu.color }}>
                      GPA: {edu.gpa}
                    </span>
                  </div>
                </div>
              </div>

              <p className="edu-description">{edu.description}</p>

              <div className="edu-section">
                <h5 className="section-subtitle">
                  <span className="comment">// Relevant Coursework</span>
                </h5>
                <div className="courses-grid">
                  {edu.courses.map((course, i) => (
                    <motion.span
                      key={course}
                      className="course-tag"
                      initial={{ opacity: 0, scale: 0 }}
                      animate={inView ? { opacity: 1, scale: 1 } : {}}
                      transition={{ delay: 0.3 + index * 0.2 + i * 0.05 }}
                      whileHover={{ scale: 1.05, backgroundColor: edu.color }}
                    >
                      {course}
                    </motion.span>
                  ))}
                </div>
              </div>

              <div className="edu-section">
                <h5 className="section-subtitle">
                  <span className="comment">// Achievements</span>
                </h5>
                <ul className="achievements-list">
                  {edu.achievements.map((achievement, i) => (
                    <motion.li
                      key={i}
                      initial={{ opacity: 0, x: -20 }}
                      animate={inView ? { opacity: 1, x: 0 } : {}}
                      transition={{ delay: 0.5 + index * 0.2 + i * 0.1 }}
                    >
                      <span className="bullet">▹</span>
                      {achievement}
                    </motion.li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Certifications */}
        <motion.div
          className="certifications"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.8, duration: 0.6 }}
        >
          <h3 className="cert-title">
            <span className="comment">// Certifications & Training</span>
          </h3>
          <div className="cert-grid">
            {certifications.map((cert, index) => (
              <motion.div
                key={cert.name}
                className="cert-card"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={inView ? { opacity: 1, scale: 1 } : {}}
                transition={{ delay: 1 + index * 0.1 }}
                whileHover={{ scale: 1.05, y: -5 }}
              >
                <span className="cert-icon">{cert.icon}</span>
                <div className="cert-info">
                  <h4 className="cert-name">{cert.name}</h4>
                  <p className="cert-issuer">{cert.issuer}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Education;
